import {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from '../auth';
import * as db from '../db';
import type {Project} from '../db';
import {Navbar, UserMenu, Modal, EmptyState} from '../components';
import './Dashboard.scss';

/* ── Icons ── */

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function FilmIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
      <line x1="7" y1="2" x2="7" y2="22" />
      <line x1="17" y1="2" x2="17" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="2" y1="7" x2="7" y2="7" />
      <line x1="2" y1="17" x2="7" y2="17" />
      <line x1="17" y1="7" x2="22" y2="7" />
      <line x1="17" y1="17" x2="22" y2="17" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L14.5 8.5L20 12L14.5 15.5L12 21L9.5 15.5L4 12L9.5 8.5L12 3Z" />
    </svg>
  );
}

/* ── Relative time ── */

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/* ── Component ── */

export function Dashboard() {
  const {supabase} = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      const data = await db.listProjects(supabase);
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Auto-focus create input
  useEffect(() => {
    if (showCreate) {
      setTimeout(() => createInputRef.current?.focus(), 50);
    }
  }, [showCreate]);

  const handleCreate = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const project = await db.createProject(supabase, newName.trim());
      window.location.hash = `#/project/${project.id}`;
    } catch (err) {
      console.error('Failed to create project:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await db.deleteProject(supabase, deleteTarget.id);
      setProjects(prev => prev.filter(p => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete project:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dashboard">
      <Navbar>
        <Navbar.Left>
          <Navbar.Logo />
        </Navbar.Left>
        <Navbar.Right>
          <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
            <PlusIcon />
            New Project
          </button>
          <UserMenu />
        </Navbar.Right>
      </Navbar>

      <div className="dashboard__content">
        <div className="dashboard__header">
          <h2 className="dashboard__title">Projects</h2>
        </div>

        {loading ? (
          <div className="dashboard__skeleton-grid">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-card__preview skeleton" />
                <div className="skeleton-card__body">
                  <div className="skeleton-card__title skeleton" />
                  <div className="skeleton-card__meta skeleton" />
                </div>
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <EmptyState
            icon={<SparklesIcon />}
            title="No projects yet"
            text="Create your first project and start generating animations with AI."
            action={
              <button
                className="btn btn--primary"
                onClick={() => setShowCreate(true)}
              >
                <PlusIcon />
                Create your first project
              </button>
            }
          />
        ) : (
          <div className="dashboard__grid">
            {projects.map(project => (
              <div
                key={project.id}
                className="project-card"
                onClick={() => {
                  window.location.hash = `#/project/${project.id}`;
                }}
              >
                <div className="project-card__preview">
                  <div className="project-card__preview-icon">
                    <FilmIcon />
                  </div>
                </div>
                <div className="project-card__body">
                  <div className="project-card__info">
                    <h3 className="project-card__name">{project.name}</h3>
                    <p className="project-card__meta">
                      {timeAgo(project.updated_at)}
                    </p>
                  </div>
                  <button
                    className="btn btn--ghost btn--sm project-card__delete"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteTarget(project);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <button
              className="new-project-card"
              onClick={() => setShowCreate(true)}
            >
              <div className="new-project-card__icon">
                <PlusIcon />
              </div>
              <span className="new-project-card__label">New project</span>
            </button>
          </div>
        )}
      </div>

      {/* Create project modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setNewName(''); }}>
        <Modal.Title>New project</Modal.Title>
        <Modal.Description>Give your project a name. You can change it later.</Modal.Description>
        <form onSubmit={e => { e.preventDefault(); handleCreate(); }}>
          <input
            ref={createInputRef}
            className="input"
            type="text"
            placeholder="Project name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            autoFocus
          />
          <Modal.Actions>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => { setShowCreate(false); setNewName(''); }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!newName.trim() || creating}
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </Modal.Actions>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <Modal.Title>Delete project</Modal.Title>
        <Modal.Description>
          Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
        </Modal.Description>
        <Modal.Actions>
          <button
            className="btn btn--ghost"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            className="btn btn--danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete project'}
          </button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
